import React, { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faArrowLeft, 
  faArrowRight,
  faMagic,
  faFilm,
  faTimes,
  faCopy,
  faEdit,
  faSave,
  faRefresh,
  faFileText
} from '@fortawesome/free-solid-svg-icons';
import styles from './ScriptGenerator.module.css';
import { useNotification } from '../../context/NotificationContext';
import { getApiUrl } from '../../config/api';
import { useScriptToPromptNavigation } from '../../hooks/useScriptToPromptNavigation';
import { ScriptToPromptModal } from './components';
import { ScriptRegenerateService } from './services/scriptRegenerateService';

// Import types and config
import { Question, ScriptGeneratorProps, AnswersState } from './types';
import { questions } from './config/questions';
import { ScriptRegenerationService } from './services/scriptRegenerationService';

const ScriptGenerator: React.FC<ScriptGeneratorProps> = ({ 
  onScriptCreated, 
  onNavigateToProject 
}) => {
  // Extract projectId from URL parameters
  const { projectId } = useParams<{ projectId: string }>();
  const location = useLocation();
  const { t, i18n } = useTranslation('scriptGenerator');
  
  // Core state
  const [currentQuestionId, setCurrentQuestionId] = useState<string>('scriptType');
  const [answers, setAnswers] = useState<AnswersState>({});
  const [isGenerating, setIsGenerating] = useState(false);
  const [showResults, setShowResults] = useState(false);
  
  // Script state
  const [generatedScript, setGeneratedScript] = useState('');
  const [editedScript, setEditedScript] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [userIdea, setUserIdea] = useState('');
  const [scriptId, setScriptId] = useState<string | null>(null); // Keep as scriptId for state variable name

  const { showNotification } = useNotification();
  // Hook for script-to-prompt navigation (NEW FEATURE - keeping this)
  const {
    showModal,
    setShowModal,
    handleProceedWithScript,
    handleProceedWithoutScript
  } = useScriptToPromptNavigation(projectId);

  // Handle auto-opening prompt modal from navigation (NEW FEATURE - keeping this)
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('openPromptModal') === 'true') {
      // Remove the query parameter and open modal
      const newUrl = location.pathname;
      window.history.replaceState({}, '', newUrl);
      setShowModal(true);
    }
  }, [location.search]);

  // Filter questions based on dependencies (RESTORED ORIGINAL LOGIC)
  const getRelevantQuestions = (): Question[] => {
    return questions.filter(question => {
      if (!question.dependsOn) return true;
      
      const dependencyValue = answers[question.dependsOn];
      if (!dependencyValue) return false;
      
      if (Array.isArray(question.dependsOnValue)) {
        return question.dependsOnValue.includes(dependencyValue as string);
      }
      
      return dependencyValue === question.dependsOnValue;
    });
  };

  const relevantQuestions = getRelevantQuestions();
  const currentQuestionIndex = relevantQuestions.findIndex(q => q.id === currentQuestionId);
  const currentQuestion = relevantQuestions[currentQuestionIndex];
  const isLastQuestion = currentQuestionIndex === relevantQuestions.length - 1;
  const isFirstQuestion = currentQuestionIndex === 0;

  // Can proceed logic (RESTORED ORIGINAL ROBUST VALIDATION)
  const canProceed = currentQuestion && (
    !currentQuestion.required || 
    (currentQuestion.type === 'single' && answers[currentQuestion.id] !== undefined) ||
    (currentQuestion.type === 'text' && 
     typeof answers[currentQuestion.id] === 'string' && 
     (answers[currentQuestion.id] as string).trim().length > 0)
  );

  const handleAnswer = (answer: string | string[]) => {
    setAnswers(prev => ({
      ...prev,
      [currentQuestion.id]: answer
    }));
  };

  const handleNext = () => {
    if (isLastQuestion) {
      // Show the user idea input before generating
      if (!userIdea.trim()) {
        showNotification({
          title: t('scriptGenerator.notifications.userIdeaRequired.title'),
          message: t('scriptGenerator.notifications.userIdeaRequired.message'),
          type: 'error'
        });
        return;
      }
      handleGenerate();
    } else if (currentQuestionIndex < relevantQuestions.length - 1) {
      setCurrentQuestionId(relevantQuestions[currentQuestionIndex + 1].id);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionId(relevantQuestions[currentQuestionIndex - 1].id);
    }
  };
  const handleGenerate = async () => {
    setIsGenerating(true);
    
    try {
      //console.log('ScriptGenerator: projectId from URL:', projectId);
      
      if (!projectId) {
        console.error('ScriptGenerator: No projectId found in URL');
        showNotification({
          message: 'Project ID is missing. Please navigate back and try again.',
          type: 'error'
        });
        setIsGenerating(false);
        return;
      }
      
      // RESTORED ORIGINAL STRUCTURED API PAYLOAD
      // Combine userIdea with optional userNarration
      let fullUserIdea = userIdea.trim();
      
      // If user provided narration, append it with a marker
      if (answers.userNarration && (answers.userNarration as string).trim()) {
        const polishEnabled = answers.userNarration_polish === true;
        const polishInstruction = polishEnabled 
          ? '(AI: Polish and enhance this narration while keeping the core message)'
          : '(AI: Keep this narration EXACTLY as written - only fix critical grammar/spelling)';
        
        fullUserIdea += `\n\n####USER NARRATION#### ${polishInstruction}\n${(answers.userNarration as string).trim()}`;
      }
      
      const generateData = {
        projectId,
        userIdea: fullUserIdea,
        language: i18n.language, // Add current UI language (en/fr)
        format: answers.scriptType as string,
        duration: answers.duration as string,
        audience: answers.audience as string,
        tone: answers.tone as string,
        genre: answers.genre as string,
        setting: answers.setting as string,
        characters: answers.characters ? [answers.characters as string] : undefined,
        additionalOptions: {
          keyPoints: answers.keyPoints,
          callToAction: answers.callToAction,
        }
      };

      //console.log('ScriptGenerator: Sending payload:', generateData);

      const response = await fetch(getApiUrl('/script-generator/generate'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(generateData)
      });

      const result = await response.json();

      if (result.success) {
        //console.log('📝 Script Generation Response:', result);
        
        // The id is in result.id
        const scriptId = result.id;
        //console.log('📝 Capturing scriptId:', scriptId);
        
        setGeneratedScript(result.generatedScript);
        setEditedScript(result.generatedScript);
        setScriptId(scriptId); // Store the script ID
        setShowResults(true);
        
        // showNotification({
        //   title: 'Success',
        //   message: 'Script generated successfully!',
        //   type: 'success'
        // });

        if (onScriptCreated) {
          onScriptCreated(scriptId);
        }
      } else {
        throw new Error(result.message || 'Failed to generate script');
      }
    } catch (error) {
      console.error('Error generating script:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      showNotification({
        title: t('scriptGenerator.notifications.generateError.title'),
        message: `${t('scriptGenerator.notifications.generateError.message')} ${errorMessage}`,
        type: 'error'
      });
    } finally {
      setIsGenerating(false);
    }
  };
  const handleCopyScript = () => {
    navigator.clipboard.writeText(isEditing ? editedScript : generatedScript);
    showNotification({
      title: t('scriptGenerator.notifications.copied.title'),
      message: t('scriptGenerator.notifications.copied.message'),
      type: 'success'
    });
  };

  const handleSaveEdit = () => {
    setGeneratedScript(editedScript);
    setIsEditing(false);
    showNotification({
      title: t('scriptGenerator.notifications.saveSuccess.title'),
      message: t('scriptGenerator.notifications.saveSuccess.message'),
      type: 'success'
    });
  };

  const handleRegenerate = async () => {
    //console.log('🔥🔥🔥 REGENERATE BUTTON CLICKED 🔥🔥🔥');
    setIsGenerating(true);
    
    try {
      // Use shared service to build payload
      const payload = ScriptRegenerateService.buildFromAnswers(
        projectId!,
        userIdea,
        answers,
        scriptId || undefined,
        i18n.language // Pass current language for regeneration
      );

      //console.log('🔄 Using shared service payload:', payload);

      // Use shared service to regenerate
      const result = await ScriptRegenerateService.regenerateScript(payload);
      
      if (result.success) {
        const newScript = ScriptRegenerateService.extractGeneratedScript(result);
        const newScriptId = result.id; // Get id from response
        
        if (newScript) {
          setGeneratedScript(newScript);
          setEditedScript(newScript);
          setIsEditing(false);
        }
        
        if (newScriptId) {
          setScriptId(newScriptId);
          //console.log('🔄 Updated scriptId after regeneration:', newScriptId);
        }
        
        // showNotification({
        //   title: 'Success',
        //   message: 'Script regenerated successfully!',
        //   type: 'success'
        // });
      } else {
        throw new Error(result.message || 'Failed to regenerate script');
      }
    } catch (error) {
      console.error('Error regenerating script:', error);
      showNotification({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to regenerate script',
        type: 'error'
      });
    } finally {
      setIsGenerating(false);
    }
  };

  // RESTORED ORIGINAL RENDER FUNCTION
  const renderQuestion = () => {
    if (!currentQuestion) return null;

    // Get translated question text
    const questionText = currentQuestion.questionKey 
      ? t(currentQuestion.questionKey) 
      : currentQuestion.question;

    return (
      <div className={styles.questionContainer}>
        <h2 className={styles.questionTitle}>{questionText}</h2>
        
        {currentQuestion.type === 'single' && (
          <div className={styles.optionsGrid}>
            {currentQuestion.options?.map((option) => {
              // Get translated option text
              const optionText = currentQuestion.optionsKey
                ? t(`${currentQuestion.optionsKey}.${option}`)
                : option;
              
              return (
                <button
                  key={option}
                  className={`${styles.optionButton} ${
                    answers[currentQuestion.id] === option ? styles.selected : ''
                  }`}
                  onClick={() => handleAnswer(option)}
                >
                  {optionText}
                </button>
              );
            })}
          </div>
        )}

        {currentQuestion.type === 'text' && (
          <>
            <textarea
              className={styles.textInput}
              placeholder={currentQuestion.placeholderKey 
                ? t(currentQuestion.placeholderKey) 
                : currentQuestion.placeholder}
              value={answers[currentQuestion.id] as string || ''}
              onChange={(e) => handleAnswer(e.target.value)}
              rows={4}
            />
            
            {/* Checkbox for narration polish option */}
            {currentQuestion.hasCheckbox && currentQuestion.checkboxKey && (
              <div style={{ marginTop: '12px' }}>
                <label style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  color: '#e0e0e0'
                }}>
                  <input
                    type="checkbox"
                    checked={answers[`${currentQuestion.id}_polish`] === true}
                    onChange={(e) => {
                      setAnswers(prev => ({
                        ...prev,
                        [`${currentQuestion.id}_polish`]: e.target.checked
                      }));
                    }}
                    style={{
                      width: '16px',
                      height: '16px',
                      cursor: 'pointer'
                    }}
                  />
                  <span>{t(currentQuestion.checkboxKey)}</span>
                </label>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  if (showResults) {
    return (
      <div className={styles.container}>
        <div className={`${styles.resultsContainer} ${isGenerating ? styles.generating : ''}`}>
          {/* Loading Overlay */}
          {isGenerating && (
            <div className={styles.loadingOverlay}>
              <div className={styles.loadingContent}>
                <div className={styles.spinner}></div>
                <p className={styles.loadingText}>{t('scriptGenerator.buttons.generating')}</p>
              </div>
            </div>
          )}
          
          <div className={styles.resultsHeader}>
            <h2 className={styles.resultsTitle}>
              <FontAwesomeIcon icon={faFilm} />
              {isEditing ? t('scriptGenerator.results.editMode') : t('scriptGenerator.results.title')}
            </h2>
            <div className={styles.resultsActions}>
              <button
                className={styles.actionButton}
                onClick={handleCopyScript}
                disabled={isGenerating}
                title="Copy script"
              >
                <FontAwesomeIcon icon={faCopy} />
              </button>
              <button
                className={`${styles.actionButton} ${isGenerating ? styles.regenerating : ''}`}
                onClick={handleRegenerate}
                disabled={isGenerating}
                title="Regenerate script"
              >
                {isGenerating ? (
                  <>
                    <div className={styles.buttonSpinner}></div>
                    Regenerating...
                  </>
                ) : (
                  <FontAwesomeIcon icon={faRefresh} />
                )}
              </button>
              <button
                className={styles.actionButton}
                onClick={() => setIsEditing(!isEditing)}
                disabled={isGenerating}
                title="Edit script"
              >
                <FontAwesomeIcon icon={faEdit} />
              </button>
            </div>
          </div>

          <div className={`${styles.scriptContent} ${isGenerating ? styles.dimmed : ''}`}>
            {isEditing ? (
              <textarea
                className={styles.scriptEditor}
                value={editedScript}
                onChange={(e) => setEditedScript(e.target.value)}
                rows={20}
                disabled={isGenerating}
              />
            ) : (
              <div className={styles.scriptDisplay}>
                {generatedScript.split('\n').map((line, index) => (
                  <p key={index} className={styles.scriptLine}>
                    {line || '\u00A0'}
                  </p>
                ))}
              </div>
            )}
          </div>

          <div className={styles.resultsFooter}>
            {isEditing && (
              <button
                className={styles.saveButton}
                onClick={handleSaveEdit}
              >
                <FontAwesomeIcon icon={faSave} />
                Save Changes
              </button>
            )}
            
            <button
              className={styles.newScriptButton}
              onClick={() => {
                setShowResults(false);
                setCurrentQuestionId('scriptType');
                setAnswers({});
                setUserIdea('');
                setIsEditing(false);
              }}
            >
              Generate Another Script
            </button>
            
            {/* TODO: Fix modal rendering issue - button logic works but modal doesn't appear */}
            {/* 
            <button
              className={styles.promptButton}
              onClick={() => {
                console.log('🔍 Load into Prompt Generator clicked');
                console.log('📁 Project ID:', projectId);
                console.log('📄 Generated Script:', !!generatedScript);
                console.log('🔄 Current showModal state:', showModal);
                setShowModal(true);
                console.log('✅ setShowModal(true) called');
              }}
            >
              <FontAwesomeIcon icon={faFileText} />
              Load into Prompt Generator
            </button>
            */}
            
            {onNavigateToProject && (
              <button
                className={styles.projectButton}
                onClick={onNavigateToProject}
              >
                Back to Project
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>
          <FontAwesomeIcon icon={faFilm} />
          {t('scriptGenerator.header.title')}
        </h1>
        <p className={styles.subtitle}>
          {t('scriptGenerator.header.subtitle')}
        </p>
      </div>

      <div className={styles.progress}>
        <div className={styles.progressBar}>
          <div 
            className={styles.progressFill}
            style={{ width: `${((currentQuestionIndex + 1) / relevantQuestions.length) * 100}%` }}
          />
        </div>
        <span className={styles.progressText}>
          {currentQuestionIndex + 1} of {relevantQuestions.length}
        </span>
      </div>

      <div className={styles.questionSection}>
        {renderQuestion()}
        
        {isLastQuestion && (
          <div className={styles.userIdeaSection}>
            <h3 className={styles.userIdeaTitle}>{t('scriptGenerator.userIdea.title')}</h3>
            <textarea
              className={styles.userIdeaInput}
              placeholder={t('scriptGenerator.userIdea.placeholder')}
              value={userIdea}
              onChange={(e) => setUserIdea(e.target.value)}
              rows={6}
            />
          </div>
        )}
      </div>

      <div className={styles.navigationButtons}>
        <button
          className={styles.navButton}
          onClick={handlePrevious}
          disabled={isFirstQuestion}
        >
          <FontAwesomeIcon icon={faArrowLeft} />
          {t('scriptGenerator.buttons.previous')}
        </button>

        <button
          className={`${styles.navButton} ${styles.nextButton}`}
          onClick={handleNext}
          disabled={!canProceed || (isLastQuestion && !userIdea.trim())}
        >
          {isGenerating ? (
            <>
              <div className={styles.spinner} />
              {t('scriptGenerator.buttons.generating')}
            </>
          ) : isLastQuestion ? (
            <>
              <FontAwesomeIcon icon={faMagic} />
              {t('scriptGenerator.buttons.generateScript')}
            </>
          ) : (
            <>
              {t('scriptGenerator.buttons.next')}
              <FontAwesomeIcon icon={faArrowRight} />
            </>
          )}
        </button>
      </div>

      {/* Script to Prompt Modal (NEW FEATURE - keeping this) */}
      <ScriptToPromptModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        projectId={projectId || 'fallback-project-id'}
        onProceedWithScript={handleProceedWithScript}
        onProceedWithoutScript={handleProceedWithoutScript}
      />
    </div>
  );
};

export default ScriptGenerator;